import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { PRICING_COLUMN_TYPES } from '../../../../constants/FieldPricingTableColumnTypes';
import PricingTableSummaryRow from './PricingTableSummaryRow';
import {
  getValueType,
  parseDisplayValue,
  formatCellValue,
  getInitialInputValues,
  formatSummaryColumnValues,
  formatUnfocusedSummaryValues,
  calculateSummaryData,
  isFormattedCorrectly
} from './pricingHelpers';

const SummaryContainer = styled.div`
  margin-top: 1rem;
  background-color: ${(props) => props.backgroundColor || props.theme?.backgroundColor || 'white'};
  border: 1px solid ${(props) => props.borderColor || props.theme?.borderColor || '#e2e8f0'};
  border-radius: ${(props) => {
    if (props.borderRadius) {
      return `${props.borderRadius}px`;
    } else if (props.theme?.borderRadius) {
      return `${props.theme.borderRadius}px`;
    }
    return '0.5rem';
  }};
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  padding: ${(props) => (props.padding ? `${props.padding}px` : '15px 12px 15px 15px')};
  width: 215px;
  margin-left: auto;
  margin-right: 7px;
  font-size: ${(props) => (props.fontSize ? `${props.fontSize}px` : 'inherit')};
  color: ${(props) => props.fontColor || props.theme?.fontColorPrimary || 'inherit'};
  font-style: ${(props) => props.fontStyle || 'normal'};
  font-weight: ${(props) => props.fontWeight || 'normal'};
  text-align: ${(props) => props.textAlign || 'right'};
`;

const EmptyStateMessage = styled.div`
  text-align: center;
  padding: 1rem 0;
  color: ${(props) => props.theme?.fontColorSecondary || '#718096'};
  font-size: 0.875rem;
  font-style: italic;
`;

const AddSummaryButton = styled.button`
  background-color: ${(props) => props.theme?.backgroundColor || '#f7fafc'};
  border: 1px solid ${(props) => props.theme?.borderColor || '#e2e8f0'};
  border-radius: ${(props) => (props.theme?.borderRadius ? `${props.theme.borderRadius}px` : '0.25rem')};
  padding: 0.5rem;
  margin-top: 0.5rem;
  width: 100%;
  font-size: 0.875rem;
  color: ${(props) => props.theme?.fontColorPrimary || '#4a5568'};
  cursor: pointer;
`;

// Helper to get the default label based on column type
const getDefaultLabelForType = (type) => {
  switch (type) {
    case PRICING_COLUMN_TYPES.SUBTOTAL:
      return 'Subtotal';
    case PRICING_COLUMN_TYPES.TAX:
      return 'Tax';
    case PRICING_COLUMN_TYPES.FEE:
      return 'Fee';
    case PRICING_COLUMN_TYPES.DISCOUNT:
      return 'Discount';
    case PRICING_COLUMN_TYPES.TOTAL:
      return 'Total';
    default:
      return '';
  }
};

const PricingTableSummary = ({
  columns,
  summaryColumns = [],
  onSummaryCellChange,
  onChangeSummaryColumnFormat,
  onDeleteSummaryColumn,
  onInsertSummaryColumn,
  canManageColumns = true,
  mode,
  formMode,
  // Style props
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  backgroundColor,
  borderColor,
  borderWidth,
  borderRadius,
  padding,
  theme
}) => {
  const [focusedInputs, setFocusedInputs] = useState({});
  const [inputValues, setInputValues] = useState({});

  // Get current mode (edit/fill/readonly)
  const currentMode = mode;

  // Allow summary cell editing regardless of mode, as long as canManageColumns is true
  const canEditSummary = canManageColumns;

  // Re-Calculate all the summary values
  const summaryData = useMemo(() => {
    return calculateSummaryData(columns, summaryColumns);
  }, [columns, summaryColumns]);

  // Initialize input values when summary columns change
  useEffect(() => {
    const initialInputValues = getInitialInputValues(summaryColumns);
    setInputValues(initialInputValues);
  }, [summaryColumns]);

  // Format all values on initial load
  useEffect(() => {
    if (!onSummaryCellChange) return;

    const updates = formatSummaryColumnValues(summaryColumns);
    updates.forEach((update) => {
      onSummaryCellChange(update.columnIndex, update.formattedValue);
    });
  }, [summaryColumns, onSummaryCellChange, columns]);

  // Format values when column format changes
  useEffect(() => {
    if (!onSummaryCellChange) return;

    const updates = formatUnfocusedSummaryValues(summaryColumns, focusedInputs);

    // Apply all updates
    updates.forEach((update) => {
      onSummaryCellChange(update.columnIndex, update.formattedValue);
    });
  }, [summaryColumns, onSummaryCellChange, focusedInputs, columns]);

  // Add an immediate formatting effect that runs once on mount
  useEffect(() => {
    if (!summaryColumns.length) return;

    // Format all summary values immediately to ensure proper display
    summaryColumns.forEach((column, index) => {
      const valueType = column.format === 'percent' ? 'percent' : 'amount';
      const value = column.value || summaryData[column._id] || '0';

      if (!isFormattedCorrectly(value, valueType)) {
        const formattedValue = formatCellValue(value, valueType);
        if (onSummaryCellChange) {
          onSummaryCellChange(index, formattedValue);
        }
      }
    });
  }, [summaryColumns, summaryData, onSummaryCellChange]);

  const handleInputFocus = (columnId, column) => {
    if (!column || !canEditSummary) return;

    const valueType = getValueType(column);
    setFocusedInputs((prev) => ({ ...prev, [columnId]: true }));

    const rawValue = parseDisplayValue(column.value || '0', valueType);
    setInputValues((prev) => ({
      ...prev,
      [columnId]: rawValue
    }));
  };

  const handleInputChange = (columnId, value) => {
    if (!canEditSummary) return;
    setInputValues((prev) => ({ ...prev, [columnId]: value }));
  };

  const handleInputBlur = (columnId, column) => {
    if (!column || !onSummaryCellChange || !canEditSummary) return;

    setFocusedInputs((prev) => ({ ...prev, [columnId]: false }));

    const valueType = getValueType(column);
    const formattedValue = formatCellValue(inputValues[columnId] || '0', valueType);

    const columnIndex = summaryColumns.findIndex((col) => col._id === column._id);
    if (columnIndex !== -1) {
      onSummaryCellChange(columnIndex, formattedValue);
    }
  };

  const handleFormatChange = (columnId, newFormat) => {
    if (!onChangeSummaryColumnFormat || !canEditSummary) return;

    // Find the column and its current format
    const columnIndex = summaryColumns.findIndex((col) => col._id === columnId);
    if (columnIndex !== -1) {
      // Call the parent's onChangeSummaryColumnFormat function to update the format
      // This will handle both updating the format and converting the value
      onChangeSummaryColumnFormat(columnId, newFormat);
    }
  };

  const getRowMenuItems = (itemId, position) => {
    // Allow operations based only on canManageColumns and fieldMode
    if (!canManageColumns) return [];

    const column = summaryColumns.find((col) => col._id === itemId);
    const isSubtotalOrTotal = column && (column.type === PRICING_COLUMN_TYPES.SUBTOTAL || column.type === PRICING_COLUMN_TYPES.TOTAL);

    const baseMenuItems = [
      {
        _id: `insert-${itemId}`,
        label: 'Insert Below',
        submenu: [
          {
            _id: `insert-tax-${itemId}`,
            label: 'Tax',
            onClick: () => onInsertSummaryColumn(PRICING_COLUMN_TYPES.TAX, position + 1)
          },
          {
            _id: `insert-fee-${itemId}`,
            label: 'Fee',
            onClick: () => onInsertSummaryColumn(PRICING_COLUMN_TYPES.FEE, position + 1)
          },
          {
            _id: `insert-discount-${itemId}`,
            label: 'Discount',
            onClick: () => onInsertSummaryColumn(PRICING_COLUMN_TYPES.DISCOUNT, position + 1)
          },
          {
            _id: `insert-total-${itemId}`,
            label: 'Total',
            onClick: () => onInsertSummaryColumn(PRICING_COLUMN_TYPES.TOTAL, position + 1)
          },
          {
            _id: `insert-subtotal-${itemId}`,
            label: 'Subtotal',
            onClick: () => onInsertSummaryColumn(PRICING_COLUMN_TYPES.SUBTOTAL, position + 1)
          }
        ]
      }
    ];

    // Only add format options for non-subtotal/total rows
    if (!isSubtotalOrTotal) {
      baseMenuItems.push(
        {
          _id: `format-amount-${itemId}`,
          label: 'Currency ($)',
          onClick: () => handleFormatChange(itemId, 'amount')
        },
        {
          _id: `format-percent-${itemId}`,
          label: 'Percent (%)',
          onClick: () => handleFormatChange(itemId, 'percent')
        }
      );
    }

    baseMenuItems.push({
      _id: `remove-${itemId}`,
      label: 'Remove',
      isDelete: true,
      onClick: () => onDeleteSummaryColumn(itemId)
    });

    return baseMenuItems;
  };

  const hasSummaryRows = summaryColumns.length > 0;

  const handleAddFirstSummaryRow = () => {
    // Allow adding summary row based only on canManageColumns
    if (!onInsertSummaryColumn || !canManageColumns) return;
    onInsertSummaryColumn(PRICING_COLUMN_TYPES.TOTAL);
  };

  return (
    <SummaryContainer
      fontSize={fontSize}
      fontColor={fontColor}
      fontStyle={fontStyle}
      fontWeight={fontWeight}
      textAlign={textAlign}
      textTransform={textTransform}
      textDecoration={textDecoration}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderWidth={borderWidth}
      borderRadius={borderRadius}
      padding={padding}
      theme={theme}
    >
      {!hasSummaryRows && canManageColumns ? (
        <>
          <EmptyStateMessage theme={theme}>No summary rows</EmptyStateMessage>
          <AddSummaryButton onClick={handleAddFirstSummaryRow} theme={theme}>
            Add Summary
          </AddSummaryButton>
        </>
      ) : (
        <>
          {summaryColumns.map((column, index) => {
            const label = column.title || getDefaultLabelForType(column.type);
            const isLast = index === summaryColumns.length - 1;
            const isBold = column.type === PRICING_COLUMN_TYPES.TOTAL;
            const valueType = column.format === 'percent' ? 'percent' : 'amount';

            // Always use calculated values for SUBTOTAL and TOTAL columns
            let displayValue;
            if (column.type === PRICING_COLUMN_TYPES.SUBTOTAL || column.type === PRICING_COLUMN_TYPES.TOTAL) {
              const calculatedValue = summaryData[column._id] || '0';
              displayValue = isFormattedCorrectly(calculatedValue, valueType)
                ? calculatedValue
                : formatCellValue(calculatedValue, valueType);
            } else {
              displayValue = column.value || '0';
              if (!isFormattedCorrectly(displayValue, valueType)) {
                displayValue = formatCellValue(displayValue, valueType);
              }
            }

            // Add this line to check if column is read-only (subtotal or total)
            const isReadOnly = column.type === PRICING_COLUMN_TYPES.SUBTOTAL || column.type === PRICING_COLUMN_TYPES.TOTAL;

            return (
              <PricingTableSummaryRow
                key={column._id}
                label={label}
                value={displayValue}
                format={column.format}
                valueType={valueType}
                isLast={isLast}
                isBold={isBold}
                isReadOnly={isReadOnly}
                onInputFocus={() => handleInputFocus(column._id, column)}
                onInputChange={(value) => handleInputChange(column._id, value)}
                onInputBlur={() => handleInputBlur(column._id, column)}
                inputValue={focusedInputs[column._id] ? inputValues[column._id] || '' : ''}
                isFocused={!!focusedInputs[column._id]}
                canManageColumns={canManageColumns}
                menuItems={getRowMenuItems(column._id, index)}
                mode={currentMode}
                formMode={formMode}
                fontSize={fontSize}
                fontColor={fontColor}
                fontStyle={fontStyle}
                fontWeight={fontWeight}
                textAlign={textAlign}
                textTransform={textTransform}
                textDecoration={textDecoration}
                backgroundColor={backgroundColor}
                borderColor={borderColor}
                borderWidth={borderWidth}
                borderRadius={borderRadius}
                padding={padding}
                theme={theme}
              />
            );
          })}
        </>
      )}
    </SummaryContainer>
  );
};

export default PricingTableSummary;

PricingTableSummary.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    format: PropTypes.string,
    hidden: PropTypes.bool,
    cells: PropTypes.array
  })).isRequired,
  summaryColumns: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    type: PropTypes.string,
    format: PropTypes.string,
    value: PropTypes.any,
    label: PropTypes.string
  })),
  onSummaryCellChange: PropTypes.func.isRequired,
  onChangeSummaryColumnFormat: PropTypes.func,
  onDeleteSummaryColumn: PropTypes.func,
  onInsertSummaryColumn: PropTypes.func,
  canManageColumns: PropTypes.bool,
  mode: PropTypes.string,
  formMode: PropTypes.string,
  // Style props
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontColor: PropTypes.string,
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textAlign: PropTypes.string,
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  borderWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  theme: PropTypes.object
};

